import { defineStore } from "pinia";
import axios from "@/app/plugins/axios";
import {
  Product,
  ProductsStore,
  ProductPayload,
  ProductFilterCondition,
  ProductSearchOptions,
  PaginatedProductsResponse,
  ProductAdjustInventoryResponse,
} from "@/catalog/types/products";
import type { QRCodeProduct } from "@/catalog/types/qrCodeExport";
import type { Price } from "@/catalog/types/prices";
import type { AdjustInventoryRequest } from "@nectarpay/api-contracts";
import { useSnackBarStore } from "@/shared/stores/snackbar";
import i18n from "@/app/plugins/i18n";
import { useUploadFile } from "@/shared/stores/uploadFile";
import { ref, uploadBytes, getDownloadURL, deleteObject, listAll } from "firebase/storage";
import { storage } from "@/app/plugins/firebase";
import { useAccountStore } from "@/account/stores/account";
import { useLocationStore } from "@/terminals/stores/locations";
import default_product_image from "@/shared/assets/default_product_image.png";
import router from "@/app/router";
import { TaxCodes } from "@/shared/enums/taxCodes.enum";
import { getErrorMessage } from "@/shared/utils/errorMessage";

// Key for localStorage
const STORAGE_KEY_FILTERS = "products-route-filters";

// Shape of the image objects managed by the upload flow (see useUploadFile)
export interface UploadImageItem {
  url: string;
  file?: File;
  raw?: File;
  blob?: File;
  isDefault?: boolean;
  isProductClass?: boolean;
}

export interface UploadedImage {
  url: string;
  isDefault: boolean;
}

const isLocationBasedInventoryEnabled = (): boolean =>
  useAccountStore().account?.accountSettings?.inventory?.location_based_inventory_enabled === true;

export const useProductsStore = defineStore("product", {
  state: (): ProductsStore => ({
    products: [],
    images: [],
    productPayload: {},
    selectedProduct: null,
    loading: false,
    filterProductCondition: {},
    routeFilterMap: {},
    listLoading: false,
    noResults: false,
    hasMore: false,
    hasNext: false,
    hasPrev: false,
    nextPage: "",
  }),

  // New lifecycle hooks for persistence
  hydrate(storeState: ProductsStore) {
    // Load saved routeFilterMap from localStorage if available
    try {
      const savedFilters = localStorage.getItem(STORAGE_KEY_FILTERS);
      if (savedFilters) {
        const parsedFilters: unknown = JSON.parse(savedFilters);

        // Basic validation - make sure it's an object
        if (parsedFilters && typeof parsedFilters === "object" && !Array.isArray(parsedFilters)) {
          storeState.routeFilterMap = parsedFilters as Record<string, ProductFilterCondition>;
        }
      }
    } catch {
      // If there was an error, initialize with empty object
      storeState.routeFilterMap = {};

      try {
        // Attempt to clean up corrupted storage
        localStorage.removeItem(STORAGE_KEY_FILTERS);
      } catch {
        // Unable to clean up localStorage
      }
    }
  },

  getters: {
    isLoading: (state) => state.loading,
    isListLoading: (state) => state.listLoading,
    getProducts: (state) => state.products,
    getSelectedProduct: (state) => state.selectedProduct,
  },

  actions: {
    setSelectedProduct(product: Product): void {
      this.selectedProduct = product;
    },
    setSelectedProductNull(): void {
      this.selectedProduct = null;
    },
    setFilterProductCondition(condition: ProductFilterCondition): void {
      this.filterProductCondition = condition;
    },

    // New method to save current filters for a specific route
    saveRouteFilters(routeName: string): void {
      // Only save if there are actually filters to save
      if (Object.keys(this.filterProductCondition).length > 0) {
        this.routeFilterMap[routeName] = { ...this.filterProductCondition };

        // Also save to localStorage for persistence across page reloads
        try {
          localStorage.setItem(STORAGE_KEY_FILTERS, JSON.stringify(this.routeFilterMap));
        } catch {
          // Error saving filters to localStorage - silent fail
        }
      }
    },

    // New method to restore filters for a specific route
    restoreRouteFilters(routeName: string): void {
      if (this.routeFilterMap[routeName]) {
        this.filterProductCondition = { ...this.routeFilterMap[routeName] };
      } else {
        // If no saved filters for this route, use empty object
        this.filterProductCondition = {};
      }
    },

    // New method to clear saved filters for a specific route
    clearRouteFilters(routeName: string): void {
      if (this.routeFilterMap[routeName]) {
        delete this.routeFilterMap[routeName];

        // Update localStorage
        try {
          localStorage.setItem(STORAGE_KEY_FILTERS, JSON.stringify(this.routeFilterMap));
        } catch {
          // Error updating localStorage after clearing filters - silent fail
        }
      }
      this.filterProductCondition = {};
    },

    setProductPayload(payload: ProductPayload) {
      this.productPayload = {
        ...this.productPayload,
        ...payload,
      };
    },
    clearProductPayload() {
      this.productPayload = {};
    },

    // Helper function to filter out empty values
    _filterEmptyValues(obj: ProductPayload): ProductPayload {
      const filtered: ProductPayload = {};
      const keepNullKeys = new Set(["assignedLocationIds"]);

      for (const key in obj) {
        const value = obj[key];

        if (value === undefined) {
          continue;
        }

        if (value === null) {
          if (keepNullKeys.has(key)) {
            filtered[key] = null;
          }
          continue;
        }

        // Check if the value is not null, undefined, or empty string
        // For arrays, we keep them even if empty to allow clearing arrays
        if (
          (typeof value !== "string" || value !== "") &&
          (typeof value !== "object" || Array.isArray(value) || Object.keys(value).length > 0)
        ) {
          filtered[key] = value;
        }
      }
      return filtered;
    },

    async uploadImages(
      productId: string,
      accountId: string,
      imageOwner: "products" | "productClasses" | "productTags" | boolean = "products",
    ) {
      const imagesArray = (useUploadFile().filesToUpload || []) as UploadImageItem[];
      const ownerPath =
        imageOwner === true ? "productClasses" : imageOwner === false ? "products" : imageOwner;
      const basePath = `accounts/${accountId}/${ownerPath}/${productId}/images/`;
      const storageRef = ref(storage, basePath);
      // Fetch all existing images in storage
      const listResult = await listAll(storageRef);
      //delete images not in the keep list
      const keepFileNames = new Set(
        imagesArray
          .filter((img) => img.url && img.url.startsWith("http"))
          .map((img) => {
            const decodedUrl = decodeURIComponent(img.url);
            const matches = decodedUrl.match(/images\/([^?]+)/);
            return matches ? matches[1] : null;
          })
          .filter((name): name is string => Boolean(name)),
      );

      // Loop backwards through the array so we can smoothly remove items
      for (let i = listResult.items.length - 1; i >= 0; i--) {
        const item = listResult.items[i];
        if (!keepFileNames.has(item.name)) {
          await deleteObject(item);
          listResult.items.splice(i, 1);
        }
      }

      //upload new images
      const existingDefaultImage = listResult.items.find((item) => item.name === "default.jpg");
      for (const image of imagesArray) {
        const isDefault = image.isDefault;
        // Handle newly uploaded files
        if (image.file || image.raw || image.blob) {
          const file = image.file || image.raw || image.blob;
          if (!file) continue;
          // Handle new default image upload
          if (isDefault && existingDefaultImage) {
            const newName = `${Date.now()}`;
            const newRef = ref(storage, `${basePath}${newName}`);
            const url = await getDownloadURL(existingDefaultImage);
            const response = await fetch(url);
            const blob = await response.blob();
            await uploadBytes(newRef, blob, { contentType: blob.type });
            await deleteObject(existingDefaultImage);
          }

          const path = `${basePath}${isDefault ? "default.jpg" : `${Date.now()}_${file.name}`}`;
          const fileRef = ref(storage, path);

          await uploadBytes(fileRef, file, { contentType: file.type || "image/jpeg" });
        }
        // Handle existing images from URLs
        else if (image.url.startsWith("http")) {
          const decodedUrl = decodeURIComponent(image.url);
          const matches = decodedUrl.match(/images\/([^?]+)/);
          if (!matches) continue; // skip if no match found
          const imageNameFromUrl = matches[1];
          const matchingImageRef = listResult.items.find((item) => item.name === imageNameFromUrl);
          // if the image is a product class image, set the new image to default.jpg
          if (image.isProductClass) {
            const newDefaultRef = ref(storage, `${basePath}default.jpg`);
            //download the image to a blob
            const response = await fetch(image.url);
            const blob = await response.blob();
            await uploadBytes(newDefaultRef, blob, { contentType: blob.type });
          }
          // If this existing image should become default
          if (isDefault && matchingImageRef && matchingImageRef.name !== "default.jpg") {
            // Rename existing default first (if present)
            if (existingDefaultImage) {
              const oldDefaultNewRef = ref(storage, `${basePath}${Date.now()}`);
              const oldDefaultBlob = await fetch(await getDownloadURL(existingDefaultImage)).then(
                (res) => res.blob(),
              );
              await uploadBytes(oldDefaultNewRef, oldDefaultBlob, {
                contentType: oldDefaultBlob.type,
              });
              await deleteObject(existingDefaultImage);
            }
            // Rename selected image to default.jpg
            const newDefaultBlob = await fetch(await getDownloadURL(matchingImageRef)).then((res) =>
              res.blob(),
            );
            const defaultRef = ref(storage, `${basePath}default.jpg`);
            await uploadBytes(defaultRef, newDefaultBlob, { contentType: newDefaultBlob.type });
            await deleteObject(matchingImageRef);
          }
        }
      }
      // ✅ After all operations, re-fetch the final updated images from storage
      const finalListResult = await listAll(storageRef);
      // Get fresh URLs and default status
      const finalImages = await Promise.all(
        finalListResult.items.map(async (item) => {
          return {
            url: await getDownloadURL(item),
            isDefault: item.name === "default.jpg",
          };
        }),
      );
      return finalImages;
    },

    filterImagesPayload(uploadedImages: UploadedImage[]): string[] {
      return uploadedImages.map((image) => image.url);
    },

    async createProduct() {
      try {
        this.loading = true;
        const filteredPayload = this._filterEmptyValues(this.productPayload);

        if (filteredPayload.tax_code === TaxCodes.ACCOUNT_DEFAULT) {
          filteredPayload.tax_code = null;
        }

        const response = await axios.post<string>("/product", filteredPayload);
        const productId = response.data;
        const accountId = useAccountStore().account?.id;

        if (accountId) {
          const uploadedImages = await this.uploadImages(productId, accountId);
          await axios.patch(`/product/${productId}`, {
            images: this.filterImagesPayload(uploadedImages),
          });
        }

        useSnackBarStore().toastSuccess({
          message: i18n.global.t("page.productDetails.productCreated"),
        });
        router.push({ path: `/product/${productId}` });
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
      } finally {
        this.loading = false;
      }
    },

    async updateProduct(
      updateData: ProductPayload,
      id: string | string[] | number,
      options: { redirect?: boolean; uploadImages?: boolean } = {},
    ) {
      const { redirect = true, uploadImages = true } = options;
      try {
        // Filter out empty values from updateData
        const filteredUpdateData = this._filterEmptyValues(updateData);

        if (Object.prototype.hasOwnProperty.call(updateData, "metadata")) {
          filteredUpdateData.metadata = updateData.metadata;
        }

        if (
          Object.prototype.hasOwnProperty.call(updateData, "product_class") &&
          updateData.product_class === null
        ) {
          filteredUpdateData.product_class = null;
        }

        if (filteredUpdateData.tax_code === TaxCodes.ACCOUNT_DEFAULT) {
          filteredUpdateData.tax_code = null;
        }

        const response = await axios.patch<Product>(`/product/${id}`, filteredUpdateData);

        const product = response.data;
        const productIndex = this.products?.findIndex((p: Product) => p.id === product.id);
        const existingProduct = productIndex !== -1 ? this.products[productIndex] : null;

        if (product.default_price && typeof product.default_price === "object") {
          const responseDefaultPrice = product.default_price as Price & { unitAmount?: number };
          if (
            responseDefaultPrice.unit_amount === undefined &&
            responseDefaultPrice.unitAmount !== undefined
          ) {
            responseDefaultPrice.unit_amount = responseDefaultPrice.unitAmount;
          }
        }

        if (
          existingProduct?.default_price &&
          (typeof product.default_price === "string" ||
            !product.default_price ||
            (typeof product.default_price === "object" &&
              (product.default_price as Price).unit_amount === undefined))
        ) {
          product.default_price = existingProduct.default_price;
        }

        if (productIndex !== -1) {
          this.products[productIndex] = product;
        }

        this.selectedProduct = product;

        const accountId = useAccountStore().account?.id;
        if (accountId && uploadImages) {
          const uploadedImages = await this.uploadImages(product.id, accountId);
          try {
            await axios.patch(`/product/${product.id}`, {
              images: this.filterImagesPayload(uploadedImages),
            });
          } catch (error) {
            useSnackBarStore().toastError({ message: getErrorMessage(error) });
          }
        }
        // Show success message
        useSnackBarStore().toastSuccess({
          message: i18n.global.t("page.productDetails.productUpdated"),
        });
        if (redirect) {
          router.push({ path: `/product/${id}` });
        }
        return response;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
      }
    },

    async retrieveProduct(
      id: string | string[] | number,
      doLoading = true,
    ): Promise<Product | undefined> {
      try {
        if (doLoading) this.loading = true;
        const response = await axios.get<Product>(`/product/${id}`);
        const product = response.data;
        if (product.tax_code === null) {
          product.tax_code = TaxCodes.ACCOUNT_DEFAULT;
        }
        this.selectedProduct = product;
        this.images = product.images;
        return product;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
      } finally {
        if (doLoading) this.loading = false;
      }
    },

    async retrieveProductByBarcode(barcode: string): Promise<Product | null> {
      try {
        const locationBasedInventoryEnabled = isLocationBasedInventoryEnabled();
        const selectedLocationId = useLocationStore().selectedLocation?.id;
        const locationId =
          locationBasedInventoryEnabled && typeof selectedLocationId === "string"
            ? selectedLocationId
            : undefined;

        const response = await axios.post<Product>(`/product/scan`, {
          barcode: barcode.trim(),
          ...(locationId ? { locationId } : {}),
        });
        return response.data;
      } catch {
        return null;
      }
    },

    async fetchPriceById(priceId: string): Promise<Price | 0 | undefined> {
      if (priceId === null) {
        return 0;
      }
      try {
        const response = await axios.get<Price>(`/price/${priceId}`);
        return response.data;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
      }
    },

    async updateProductPrices(products: Product[]): Promise<Product[]> {
      const pricePromises = products.map((product: Product) =>
        this.fetchPriceById(
          typeof product.default_price === "object" && product.default_price
            ? product.default_price.id
            : typeof product.default_price === "string"
              ? product.default_price
              : "",
        ),
      );

      const prices = await Promise.all(pricePromises);

      const priceMap = new Map(
        prices
          .filter((price): price is Price => typeof price === "object" && price !== null)
          .map((price) => [price.id, price.unit_amount]),
      );

      const updatedProducts = products.map((product: Product) => {
        const defaultPriceId =
          typeof product.default_price === "string" ? product.default_price : undefined;
        const resolvedAmount = defaultPriceId ? priceMap.get(defaultPriceId) : undefined;
        return {
          ...product,
          default_price: resolvedAmount ?? product.default_price,
        };
      });

      return updatedProducts;
    },

    async getFilteredProducts(apiOptions?: {
      params?: { limit?: number; page?: number };
    }): Promise<void> {
      try {
        this.listLoading = true;
        this.noResults = false;
        if (
          Object.keys(this.filterProductCondition).length === 0 ||
          (this.filterProductCondition.description === "" &&
            this.filterProductCondition.status === "")
        ) {
          const response = await axios.get<PaginatedProductsResponse>("/product", apiOptions);
          const productsFetched = response.data?.data;
          this.hasMore = response.data.has_more ?? false;
          this.listLoading = false;
          this.noResults = productsFetched.length === 0;
          this.products = [...productsFetched];
          return;
        }

        const filters: string[] = [];

        if (this.filterProductCondition.description) {
          filters.push(`description~'${this.filterProductCondition.description}'`);
        }

        if (this.filterProductCondition.status === "active") {
          filters.push(`active:'true'`);
        }

        if (this.filterProductCondition.status === "inactive") {
          filters.push(`active:'false'`);
        }

        const combinedFilter = filters.join(" AND ");
        if (combinedFilter) {
          const payload: { query: string; limit?: number; page?: number } = {
            query: combinedFilter,
            limit: apiOptions?.params?.limit,
          };

          if (apiOptions?.params?.page) {
            payload.page = apiOptions?.params?.page;
          }

          const response = await axios.post<PaginatedProductsResponse>("/product/filter", payload);
          const filteredProducts = response.data?.data;
          this.hasMore = response.data.has_more ?? false;
          this.nextPage = response.data.next_page ?? "";
          this.listLoading = false;
          this.noResults = filteredProducts.length === 0;
          this.products = [...filteredProducts];
        }
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
      } finally {
        this.listLoading = false;
      }
    },

    async listAllProducts({ allActive = false } = {}) {
      this.loading = true;

      try {
        const payload: { limit?: number; allActive?: boolean } = {
          limit: allActive ? undefined : 10,
          ...(allActive ? { allActive: true } : {}),
        };
        const response = await axios.get<PaginatedProductsResponse>("/product", {
          params: payload,
        });
        return response.data;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
      } finally {
        this.loading = false;
      }
    },

    async searchProducts(
      {
        pagination = {},
        filters = {},
        includeStoredFilters = true,
        updateStore = true,
      }: ProductSearchOptions = {},
      initLoading = false,
    ) {
      // Only set the appropriate loading flag based on the operation type
      if (initLoading) {
        this.loading = true;
      } else {
        this.listLoading = true;
      }

      try {
        const locationBasedInventoryEnabled = isLocationBasedInventoryEnabled();

        const resolvedFilters: ProductFilterCondition = {
          ...(includeStoredFilters ? this.filterProductCondition : {}), // first, use our stored state when requested
          ...filters, // then, use the argument to override
        };

        // If location-based inventory is disabled, location is not a valid filter.
        if (!locationBasedInventoryEnabled) {
          delete resolvedFilters.locationId;
        }

        // If no explicit location filter is set, default to the app bar location selection.
        if (
          locationBasedInventoryEnabled &&
          !Object.prototype.hasOwnProperty.call(resolvedFilters, "locationId")
        ) {
          const locationStore = useLocationStore();
          const selectedLocationId = locationStore.selectedLocation?.id ?? null;
          if (selectedLocationId) {
            resolvedFilters.locationId = selectedLocationId;
          }
        }

        const payload = {
          filters: {
            ...resolvedFilters,
          },
          pagination: {
            limit: 10, // default
            ...pagination,
          },
        };

        const response = await axios.post<PaginatedProductsResponse>("/product/search", payload);
        const products = response.data.data || [];

        if (updateStore) {
          this.products = [...products];
          this.hasNext = response.data.hasNext ?? false;
          this.hasPrev = response.data.hasPrev ?? false;
        }

        return products;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
      } finally {
        // Only reset the flag that was set at the beginning
        if (initLoading) {
          this.loading = false;
        } else {
          this.listLoading = false;
        }
      }
    },

    async exportProducts(customFilters: ProductFilterCondition = {}) {
      try {
        // Show loading toast
        useSnackBarStore().toastInfo({
          message:
            i18n.global.t("common.action.preparingExport") || "Preparing export, please wait...",
        });

        const locationBasedInventoryEnabled = isLocationBasedInventoryEnabled();

        const resolvedFilters: ProductFilterCondition = {
          ...this.filterProductCondition,
          ...customFilters,
        };

        // If location-based inventory is disabled, location is not a valid filter.
        if (!locationBasedInventoryEnabled) {
          delete resolvedFilters.locationId;
        }

        // If no explicit location filter is set, default to the app bar location selection.
        if (locationBasedInventoryEnabled) {
          const hasLocationIdKey = Object.prototype.hasOwnProperty.call(
            resolvedFilters,
            "locationId",
          );
          const locationIdValue = resolvedFilters.locationId;
          const hasExplicitLocationFilter =
            (hasLocationIdKey && locationIdValue === null) ||
            (typeof locationIdValue === "string" && Boolean(locationIdValue));

          if (!hasExplicitLocationFilter) {
            const locationStore = useLocationStore();
            const selectedLocationId = locationStore.selectedLocation?.id ?? null;
            if (selectedLocationId) {
              resolvedFilters.locationId = selectedLocationId;
            }
          }
        }

        // Create payload with filters but without pagination
        const payload = {
          filters: {
            ...resolvedFilters,
          },
          export: true, // Signal to the backend this is an export request
        };

        // Call the export endpoint
        const response = await axios.post("/product/export", payload, {
          responseType: "blob", // Important for handling file downloads
        });

        // Create a download link for the file
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;

        // Get the filename from the Content-Disposition header or use a default
        const contentDisposition = response.headers["content-disposition"];
        let filename = "products-export.csv";

        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1];
          }
        }

        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        useSnackBarStore().toastSuccess({
          message:
            i18n.global.t("common.action.exportProductsSuccess") ||
            "Products export completed successfully",
        });

        return true;
      } catch (error) {
        useSnackBarStore().toastError({
          message: getErrorMessage(error, "Failed to export products data"),
        });
        return false;
      }
    },

    async retrieveQRCodeProducts(filters: ProductFilterCondition = {}) {
      try {
        const locationBasedInventoryEnabled = isLocationBasedInventoryEnabled();

        const resolvedFilters: ProductFilterCondition = {
          ...this.filterProductCondition,
          ...filters,
        };

        // If location-based inventory is disabled, location is not a valid filter.
        if (!locationBasedInventoryEnabled) {
          delete resolvedFilters.locationId;
        }

        // If no explicit location filter is set, default to the app bar location selection.
        if (locationBasedInventoryEnabled) {
          const hasLocationIdKey = Object.prototype.hasOwnProperty.call(
            resolvedFilters,
            "locationId",
          );
          const locationIdValue = resolvedFilters.locationId;
          const hasExplicitLocationFilter =
            (hasLocationIdKey && locationIdValue === null) ||
            (typeof locationIdValue === "string" && Boolean(locationIdValue));

          if (!hasExplicitLocationFilter) {
            const locationStore = useLocationStore();
            const selectedLocationId = locationStore.selectedLocation?.id ?? null;
            if (selectedLocationId) {
              resolvedFilters.locationId = selectedLocationId;
            }
          }
        }

        const payload = {
          filters: {
            ...resolvedFilters,
          },
          isRawData: true,
        };

        const response = await axios.post<QRCodeProduct[]>("/product/export", payload);

        return response.data;
      } catch (error) {
        useSnackBarStore().toastError({
          message: getErrorMessage(error, "Failed to print QR codes"),
        });
      }
    },

    async adjustInventory(
      productId: string,
      adjustmentData: AdjustInventoryRequest,
    ): Promise<ProductAdjustInventoryResponse | undefined> {
      try {
        this.listLoading = true;
        const response = await axios.post<ProductAdjustInventoryResponse>(
          `/product/${productId}/adjust-inventory`,
          adjustmentData,
        );

        // Location-based adjustments update location product documents rather than global stock.
        if ("stockCount" in response.data) {
          const index = this.products.findIndex((product: Product) => product.id === productId);
          if (index !== -1) {
            this.products[index].stockCount = response.data.stockCount;
          }

          if (this.selectedProduct?.id === productId) {
            this.selectedProduct.stockCount = response.data.stockCount;
          }
        }

        return response.data;
      } catch (error) {
        useSnackBarStore().toastError({
          message: getErrorMessage(error, "Failed to adjust inventory"),
        });
        throw error;
      } finally {
        this.listLoading = false;
      }
    },

    getProductDefaultImage(images?: string[], productClass?: { images?: string[] }): string {
      const findDefault = (imgs?: string[]) =>
        imgs?.find((img) => img.includes("default.jpg")) || imgs?.[0];

      // Use product images if available
      const productImage = findDefault(images);
      if (productImage) return productImage;

      // Fallback to product class images
      const classImage = findDefault(productClass?.images);
      return classImage || default_product_image;
    },

    async checkIfProductsExist(): Promise<boolean> {
      try {
        // Perform a search with no filters and minimum results
        const response = await axios.post<PaginatedProductsResponse>("/product/search", {
          filters: {},
          pagination: {
            limit: 1, // Just need to know if any products exist
          },
        });
        return response.data.data.length > 0;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
        return false;
      }
    },
  },
});
